import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import DoughnutChart from "src/components/Charts/DoughnutChart";
import { chartOptions } from "src/resources/constant";
import { RoutePaths, StatusesColors } from "src/resources/enum";
import excellentIcon from "src/assets/icons/excellent.svg";
import goodIcon from "src/assets/icons/good.svg";
import averageIcon from "src/assets/icons/average.svg";
import weakIcon from "src/assets/icons/weak.svg";
import { formatNumber } from "src/utils/helpers";

const BMCRatingCharts = () => {
  const { t } = useTranslation(["common", "registration"]);
  const navigate = useNavigate();
  const { bmcRatingStatistics, bmcRatingTotalCount, isBMCRatingDataLoaded } =
    useSelector((state: any) => state.dashboard);
  const [chartsData, setChartsData] = useState<any>();
  const excellentSrc = excellentIcon;
  const goodSrc = goodIcon;
  const averageSrc = averageIcon;
  const weakSrc = weakIcon;

  useEffect(() => {
    const newChartsData = [
      {
        color: [StatusesColors.Excellent, StatusesColors.Planck],
        label: t("excellent"),
      },
      {
        color: [StatusesColors.Good, StatusesColors.Planck],
        label: t("good"),
      },
      {
        color: [StatusesColors.Fair, StatusesColors.Planck],
        label: t("fair"),
      },
      {
        color: [StatusesColors.Poor, StatusesColors.Planck],
        label: t("poor"),
      },
    ];

    setChartsData(newChartsData);
  }, [bmcRatingStatistics, bmcRatingTotalCount, isBMCRatingDataLoaded]);

  return (
    <Card className="card">
      <div className="d-flex justify-content-between flex-column pt-2">
        <div className="d-flex justify-content-between align-items-center ">
          <h6 className="card-title">{t("bmcsRating")}</h6>
          <Button
            className="edit-btn light-primary-btn m-0"
            onClick={() => {
              navigate(RoutePaths.BMCWithRating);
            }}
            label={t("viewCompanies")}
          />
        </div>
        <div>
          <span className="card-2-1-text-number" title={bmcRatingTotalCount}>
            {formatNumber(bmcRatingTotalCount)}
          </span>
        </div>
      </div>
      <div className="d-flex flex-column justify-content-between w-100">
        <div className="stats-row ">
          {chartsData &&
            chartsData.length > 1 &&
            (isBMCRatingDataLoaded ? (
              <>
                <div
                  className="card-2-1 w-12 "
                  title={bmcRatingStatistics.excellent}
                >
                  <DoughnutChart
                    chartData={{
                      labels: [chartsData[0].label, ""],
                      datasets: [
                        {
                          data: [
                            formatNumber(bmcRatingStatistics.excellent),
                            bmcRatingTotalCount > 0
                              ? bmcRatingTotalCount -
                                bmcRatingStatistics.excellent
                              : 1,
                          ],
                          backgroundColor: chartsData[0].color,
                          borderWidth: 0,
                          borderRadius: 50,
                        },
                      ],
                    }}
                    options={chartOptions}
                    iconObj={{
                      src: excellentSrc,
                      xOffset: 0,
                      yOffset: 10,
                      size: 33.47,
                    }}
                  />
                  <span className="font-weight-700">
                    {bmcRatingStatistics.excellent}
                  </span>
                </div>
                <div className="card-2-1 w-12" title={bmcRatingStatistics.good}>
                  <DoughnutChart
                    chartData={{
                      labels: [chartsData[1].label, ""],
                      datasets: [
                        {
                          data: [
                            formatNumber(bmcRatingStatistics.good),
                            bmcRatingTotalCount > 0
                              ? bmcRatingTotalCount - bmcRatingStatistics.good
                              : 1,
                          ],
                          backgroundColor: chartsData[1].color,
                          borderWidth: 0,
                          borderRadius: 50,
                        },
                      ],
                    }}
                    options={chartOptions}
                    iconObj={{
                      src: goodSrc,
                      xOffset: 0,
                      yOffset: 10,
                      size: 33.47,
                    }}
                  />
                  <span className="font-weight-700">
                    {bmcRatingStatistics.good}
                  </span>
                </div>
                <div className="card-2-1 w-12" title={bmcRatingStatistics.fair}>
                  <DoughnutChart
                    chartData={{
                      labels: [chartsData[2].label, ""],
                      datasets: [
                        {
                          data: [
                            formatNumber(bmcRatingStatistics.fair),
                            bmcRatingTotalCount > 0
                              ? bmcRatingTotalCount - bmcRatingStatistics.fair
                              : 1,
                          ],
                          backgroundColor: chartsData[2].color,
                          borderWidth: 0,
                          borderRadius: 50,
                        },
                      ],
                    }}
                    options={chartOptions}
                    iconObj={{
                      src: averageSrc,
                      xOffset: 0,
                      yOffset: 10,
                      size: 33.47,
                    }}
                  />
                  <span className="font-weight-700">
                    {bmcRatingStatistics.fair}
                  </span>
                </div>

                <div className="card-2-1 w-12" title={bmcRatingStatistics.poor}>
                  <DoughnutChart
                    chartData={{
                      labels: [chartsData[3].label, ""],
                      datasets: [
                        {
                          data: [
                            formatNumber(bmcRatingStatistics.poor),
                            bmcRatingTotalCount > 0
                              ? bmcRatingTotalCount - bmcRatingStatistics.poor
                              : 1,
                          ],
                          backgroundColor: chartsData[3].color,
                          borderWidth: 0,
                          borderRadius: 50,
                        },
                      ],
                    }}
                    options={chartOptions}
                    iconObj={{
                      src: weakSrc,
                      xOffset: 0,
                      yOffset: 10,
                      size: 33.47,
                    }}
                  />
                  <span className="font-weight-700">
                    {bmcRatingStatistics.poor}
                  </span>
                </div>
              </>
            ) : (
              <ProgressSpinner />
            ))}
        </div>

        <div className="stats-row mt-4 maintenance-requests-stats">
          <p className="stats-row__item">
            <span
              className="dot"
              style={{
                backgroundColor: StatusesColors.Completed,
              }}
            ></span>
            {t("excellent")}
          </p>
          <p className="stats-row__item">
            <span
              className="dot"
              style={{ backgroundColor: StatusesColors.Good }}
            ></span>
            {t("good")}
          </p>
          <p className="stats-row__item">
            <span
              className="dot"
              style={{ backgroundColor: StatusesColors.Fair }}
            ></span>
            {t("fair")}
          </p>
          <p className="stats-row__item">
            <span
              className="dot"
              style={{ backgroundColor: StatusesColors.Poor }}
            ></span>
            {t("poor")}
          </p>
        </div>
      </div>
    </Card>
  );
};

export default BMCRatingCharts;
